import { Alert } from "../../../../components/ui/Alert";
import { Card } from "../../../../components/ui/Card";
import { type WorkScheduleWindow } from "../../../../lib/api/policies";
import { weekdayLabel } from "../../../../lib/format";
import { t } from "../../../../lib/i18n";
import { useLocale } from "../../../../lib/useLocale";

interface SchedulePreviewTabProps {
  timezone: string;
  windows: WorkScheduleWindow[];
}

function minutesToHHMMLocal(minutes: number): string {
  const h = Math.floor(minutes / 60).toString().padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function nowInTimezone(timezone: string): { weekday: number; minutes: number } | null {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    });
    const parts = formatter.formatToParts(new Date());
    const wd = parts.find((p) => p.type === "weekday")?.value ?? "Mon";
    const hh = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
    const mm = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
    const map: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
    return { weekday: map[wd] ?? 0, minutes: hh * 60 + mm };
  } catch {
    return null;
  }
}

export function SchedulePreviewTab({ timezone, windows }: SchedulePreviewTabProps) {
  useLocale();
  const now = nowInTimezone(timezone);
  const isInsideWindow =
    now !== null &&
    windows.some(
      (w) =>
        w.weekday === now.weekday &&
        now.minutes >= w.start_minute &&
        now.minutes < w.end_minute,
    );

  const weekdays = [0, 1, 2, 3, 4, 5, 6];

  return (
    <Card title={t("schedules.preview.title")}>
      <div style={{ marginBottom: 12 }}>
        <Alert tone="info">{t("schedules.preview.previewIsDiagnosticOnly")}</Alert>
      </div>
      <div style={{ marginBottom: 12 }}>
        {now ? (
          <Alert tone={isInsideWindow ? "success" : "info"}>
            {isInsideWindow
              ? t("schedules.preview.currentlyInWindow")
              : t("schedules.preview.currentlyOutsideWindow")}
            {" — "}
            {timezone} {minutesToHHMMLocal(now.minutes)}
          </Alert>
        ) : (
          <Alert tone="warning">{t("schedules.preview.invalidTz")}</Alert>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "80px 1fr",
          gap: 6,
          alignItems: "center",
        }}
      >
        {weekdays.map((wd) => {
          const dayWindows = windows
            .filter((w) => w.weekday === wd)
            .sort((a, b) => a.start_minute - b.start_minute);
          return (
            <PreviewDayRow
              key={wd}
              weekday={wd}
              windows={dayWindows}
              now={now}
            />
          );
        })}
      </div>
    </Card>
  );
}

interface PreviewDayRowProps {
  weekday: number;
  windows: WorkScheduleWindow[];
  now: { weekday: number; minutes: number } | null;
}

function PreviewDayRow({ weekday, windows, now }: PreviewDayRowProps) {
  const minutesInDay = 24 * 60;
  return (
    <>
      <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
        {weekdayLabel(weekday)}
      </div>
      <div
        style={{
          position: "relative",
          height: 28,
          background: "var(--color-surface-muted)",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        {windows.map((w) => {
          const leftPct = (w.start_minute / minutesInDay) * 100;
          const widthPct = ((w.end_minute - w.start_minute) / minutesInDay) * 100;
          return (
            <div
              key={w.id}
              title={`${minutesToHHMMLocal(w.start_minute)}–${minutesToHHMMLocal(w.end_minute)}`}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                top: 0,
                bottom: 0,
                background: "var(--color-accent-soft)",
                borderLeft: "1px solid var(--color-border-strong)",
                borderRight: "1px solid var(--color-border-strong)",
              }}
            />
          );
        })}
        {now && now.weekday === weekday && (
          <div
            style={{
              position: "absolute",
              left: `${(now.minutes / minutesInDay) * 100}%`,
              top: -2,
              bottom: -2,
              width: 2,
              background: "var(--color-danger)",
            }}
            aria-label={t("schedules.preview.now")}
          />
        )}
      </div>
    </>
  );
}
